import { zodResolver } from '@hookform/resolvers/zod'
import * as Notifications from 'expo-notifications'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Linking,
  Pressable,
  StyleProp,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'
import { ButtonProps, Switch } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import { z } from 'zod'

import Alert from '@/components/shared/Alert'
import NotFound from '@/components/shared/NotFound'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import UserService from '@/services/userService'
import logger from '@/utils/datadog/log/log'
import trpc from '@/utils/trpc'

interface Props {
  buttonStyle?: StyleProp<ViewStyle>
  buttonMode?: ButtonProps['mode']
  formContainerStyle: StyleProp<ViewStyle>
  titleStyle: StyleProp<TextStyle>
}

const UserNotifications = ({
  buttonStyle,
  buttonMode,
  formContainerStyle,
  titleStyle,
}: Props) => {
  const { user, isLoading } = UserService.useCurrentUser()
  const updateSettingsMutation = trpc.user.updateSettings.useMutation()
  const [hasPermission, setHasPermission] = useState<boolean>(false)

  const defaultValues = { ...user }

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = form

  useEffect(() => {
    reset(user)
  }, [reset, user])

  useEffect(() => {
    const checkNotificationPermissions = async () => {
      const { status } = await Notifications.getPermissionsAsync()
      setHasPermission(status === 'granted')
    }
    checkNotificationPermissions()
  }, [])

  const onSubmit = async (data: FormData) => {
    updateSettingsMutation.mutate(
      {
        ...data,
        userId: user?.id || '',
      },
      {
        onSuccess: (updatedData) => {
          reset({ ...updatedData })
        },
      }
    )
  }

  const handlePushNotificationToggle = async (value: boolean) => {
    if (value && !hasPermission) {
      const { status } = await Notifications.requestPermissionsAsync()
      if (status !== 'granted') {
        logger.error('UserNotifications', {
          message:
            'Push notification permission denied, attempting to redirect user to app settings',
        })
        Alert.alert(
          'Permission Required',
          'Push notifications are disabled. Would you like to open settings to enable them?',
          [
            {
              text: 'Cancel',
              style: 'cancel',
            },
            {
              text: 'Open Settings',
              onPress: Linking.openSettings,
            },
          ]
        )
        return
      }
      setHasPermission(true)
    }
    setValue('isPushNotificationsEnabled', value, { shouldDirty: true })
  }

  if (!isLoading && !user) return <NotFound />

  return (
    <View>
      <UniText style={titleStyle} variant="titleLarge">
        Notification Preferences
      </UniText>
      <UniText style={styles.description} variant="bodyMedium">
        Stay informed about your fosters' health needs including vaccination
        schedules, medication reminders, and medical test results.
      </UniText>
      <View style={[formContainerStyle, styles.formContainer]}>
        <Pressable
          accessibilityRole="button"
          disabled={isLoading}
          onPress={() =>
            setValue(
              'isEmailNotificationsEnabled',
              !watch('isEmailNotificationsEnabled'),
              { shouldDirty: true }
            )
          }
          style={styles.settingContainer}
        >
          <View style={styles.settingTextContainer}>
            <UniText variant="bodyLarge">Email Notifications</UniText>
          </View>
          <Switch
            disabled={isLoading}
            onValueChange={(value) =>
              setValue('isEmailNotificationsEnabled', value, {
                shouldDirty: true,
              })
            }
            value={watch('isEmailNotificationsEnabled')}
          />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          disabled={isLoading}
          onPress={() =>
            handlePushNotificationToggle(!watch('isPushNotificationsEnabled'))
          }
          style={styles.settingContainer}
        >
          <View style={styles.settingTextContainer}>
            <UniText variant="bodyLarge">Push Notifications</UniText>
          </View>
          <Switch
            disabled={isLoading}
            onValueChange={handlePushNotificationToggle}
            value={watch('isPushNotificationsEnabled') && hasPermission}
          />
        </Pressable>
      </View>
      <UniButton
        disabled={isSubmitting || !isDirty}
        mode={buttonMode}
        onPress={handleSubmit(onSubmit)}
        style={buttonStyle}
      >
        Save
      </UniButton>
    </View>
  )
}

const formSchema = z.object({
  id: z.string().uuid(),
  isEmailNotificationsEnabled: z.boolean().default(true),
  isPushNotificationsEnabled: z.boolean().default(true),
})

type FormData = z.infer<typeof formSchema>

const styles = StyleSheet.create((theme) => ({
  description: {
    color: theme.colors.subtitle,
    marginBottom: theme.tokens.spacing[4],
    marginTop: theme.tokens.spacing[2],
  },
  formContainer: {
    flexDirection: 'column',
  },
  settingContainer: {
    alignItems: 'center',
    cursor: 'pointer',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.tokens.spacing[2],
    paddingVertical: theme.tokens.spacing[2],
  },
  settingTextContainer: {
    paddingRight: theme.tokens.spacing[4],
  },
}))

export default UserNotifications
